import React from "react";
import {Box} from "@mui/material";
import {fmtAccountCode} from "./numbers";

/**
 * Cuentas contables en los selectores (#14623). Conviven las del plan general (100, 626, 4751) con las
 * subcuentas de 8 dígitos de Contasol (10000000, 62600000), y a veces se llaman igual: la opción tiene que
 * dejar claro cuál es cuál, y donde se anota un apunte solo valen las que admiten movimientos.
 */
type AccountLike = { id?: number; code?: string; name?: string; allowsMovements?: boolean };

/** Del plan general: cualquier código de menos de 8 dígitos. Las subcuentas de Contasol tienen 8. */
export const isPlanAccount = (a: AccountLike) => (a.code ?? "").length < 8;

/**
 * Solo las que admiten apuntes: las agrupadoras (11, 26, 475…) no se pueden elegir en una línea. Tampoco una del
 * plan general que en la lista tenga subcuentas de 8 dígitos (#14786): con «572» salen 57200000 y 57200002, y el
 * apunte va al banco que es, no a la 572, que los informes sumarían al primero.
 */
export const postableAccounts = <T extends AccountLike>(list: T[] | undefined | null): T[] => {
    const all = list ?? [];
    const subaccounts = all.map(a => a.code ?? "").filter(c => /^\d{8}$/.test(c));
    return all.filter(a => a.allowsMovements !== false
        && !(/^\d{1,7}$/.test(a.code ?? "") && subaccounts.some(c => c.startsWith(a.code!))));
};

export const accountLabel = (a: AccountLike) => `${fmtAccountCode(a.code)} — ${a.name ?? ""}`;

/** Opción de autocompletar: código en monoespaciada, nombre y, si es del plan general, la marca «PGC». */
export const renderAccountOption = (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: AccountLike) => {
    const {key, ...rest} = props as any;
    return (
        <li {...rest} key={option.id ?? key}>
            <Box component="span" sx={{fontFamily: "monospace", mr: 1, fontSize: "0.85em", color: "text.secondary", flexShrink: 0}}>
                {fmtAccountCode(option.code)}
            </Box>
            <Box component="span" sx={{flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}}>
                {option.name}
            </Box>
            {isPlanAccount(option) && (
                <Box component="span" title="Cuenta del Plan General Contable"
                     sx={{ml: 1, px: 0.75, borderRadius: 1, border: 1, borderColor: "divider",
                         fontSize: "0.7em", lineHeight: 1.6, color: "text.secondary", flexShrink: 0}}>
                    PGC
                </Box>
            )}
        </li>
    );
};

/**
 * Código propuesto para una subcuenta nueva de `parentCode`, en el formato de 8 dígitos de Contasol: el
 * código del padre rellenado con ceros y el siguiente libre entre sus subcuentas de 8 dígitos (626 →
 * 62600001; con 62300000…62300037 → 62300038). Si el padre ya tiene 8 dígitos, no hay propuesta.
 */
export const nextSubaccountCode = (parentCode: string | undefined | null, siblingCodes: (string | undefined | null)[]): string | null => {
    const parent = (parentCode ?? "").trim();
    if (!/^\d{1,7}$/.test(parent)) return null;
    const base = parseInt(parent.padEnd(8, "0"), 10);
    const top = parseInt(String(parseInt(parent, 10) + 1).padEnd(8, "0"), 10);
    const used = siblingCodes
        .map(c => (c ?? "").trim())
        .filter(c => /^\d{8}$/.test(c) && c.startsWith(parent))
        .map(c => parseInt(c, 10));
    const next = used.length > 0 ? Math.max(...used) + 1 : base + 1;
    return next < top ? String(next).padStart(8, "0") : null;
};
